import {Injectable} from '@angular/core';
import {MenuItem} from '@models/sidebar/menu-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {TranslateService} from '@ngx-translate/core';
import {PageItem} from '@models/sidebar/page-item';
import {MenuNotification} from '@models/sidebar/menu-notification';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';
import {DesignService} from '@services/left-menu/design/design.service';
import {Role} from '@models/user/role';
import {UtilsService} from '@services/utils/utils.service';
import {EnvironmentConfig} from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MenuOverviewService {

  constructor(
      private translation: TranslateService,
      private utils: UtilsService,
      private designService: DesignService) {
  }

  public displayEditFolderButton(
      selectedChildMenuItem: MenuItem | undefined,
      hierarchyIsLoading: boolean,
      userRole: Role): boolean {
    const isAdminOrHigher = this.utils.isAdminOrHigher(userRole.isAdminRole, userRole.isTechAdminRole, userRole.isSuperAdminRole);
    if (!selectedChildMenuItem) {
      return false;
    }
    if (!selectedChildMenuItem.isEditable) {
      return false;
    }
    if (hierarchyIsLoading) {
      return false;
    }
    if (userRole.isDistributorRole || userRole.isViewerRole) {
      return false;
    }

    return !(!isAdminOrHigher && selectedChildMenuItem.isMachineGroup);
  }

  public parentTypeCategory(selectedChildMenuItem: MenuItem | undefined): string {
    if (selectedChildMenuItem?.type === MenuItemType.MACHINE) {
      return this.translation.instant('machine');
    }
    return this.translation.instant('machinery group');
  }

  public backButtonClass(selectedChildMenuItem: MenuItem | undefined, actualRootMenuItem: MenuItem | undefined): string {
    if (this.hasParent(selectedChildMenuItem, actualRootMenuItem)) {
      return 'menu-back';
    }
    return '';
  }

  public hasParent(selectedChildMenuItem: MenuItem | undefined, actualRootMenuItem: MenuItem | undefined): boolean {
    if (selectedChildMenuItem && actualRootMenuItem && selectedChildMenuItem.id !== actualRootMenuItem.id) {
      return true;
    }
    return false;
  }

  public getId(menuItem: MenuItem): string | null {

    if (!menuItem.isMachine) {
      return menuItem.id;
    }

    if (menuItem.machineId) {
      return menuItem.machineId;
    }

    const id = menuItem.id;
    if (!id) {
      return null;
    }

    if (id.indexOf('_') > 0) {
      return id.substring(id.indexOf('_') + 1, id.length);
    }
    return null;
  }

  public getPageItems(menuItems: MenuItem[], setUserId: boolean, selectedMenuItem: MenuItem, selectedChildMenuItem: MenuItem | undefined, isAdminOrHigher: boolean, firebaseUserId: string): PageItem[] {
    const pageItems: PageItem[] = [];
    const {nodeFunctionalityEnabled} = EnvironmentConfig.PortalSettings;
    for (const menuItem of menuItems) {
      if (!nodeFunctionalityEnabled && menuItem.type === MenuItemType.NODE) {
        continue;
      }
      const pageItem = new PageItem();
      pageItem.name = menuItem.name;
      pageItem.id = menuItem.id;
      pageItem.pageId = menuItem.pageId;
      pageItem.parentId = menuItem.parentId;
      pageItem.type = menuItem.type;
      pageItem.machineId = menuItem.machineId;
      pageItem.parent = menuItem.parent;
      pageItem.menuItemParent = selectedChildMenuItem;
      if (setUserId) {
        pageItem.firebaseUserId = menuItem.firebaseUserId;
      }
      pageItem.canBeAltered = this.getPageItemCanBeAltered(menuItem, selectedMenuItem, isAdminOrHigher, firebaseUserId);
      pageItem.pageId = menuItem.pageId;
      pageItem.machineCategory = menuItem.machineCategory;
      pageItem.organizationId = menuItem.organizationId;
      pageItem.url = menuItem.url;
      pageItems.push(pageItem);
    }
    return pageItems;
  }

  public getPageItemCanBeAltered(menuItem: MenuItem, selectedMenuItem: MenuItem, isAdminOrHigher: boolean, firebaseUserId: string): boolean {
    if (isAdminOrHigher) {
      return true;
    }
    if (selectedMenuItem.isMachine) {
      return menuItem.firebaseUserId === firebaseUserId;
    }
    return false;
  }

  public selectedMenuItemIsUpdated(selectedChildMenuItem: MenuItem | undefined, message: MenuNotification): boolean {
    if (selectedChildMenuItem) {
      if (selectedChildMenuItem.isMachine && String(selectedChildMenuItem.machineId) === String(message.id)) {
        return true;
      }
      if (selectedChildMenuItem.isMachineGroup && String(selectedChildMenuItem.machineGroupId) === String(message.id)) {
        return true;
      }
    }
    return false;
  }

  public getClassForNode(menuItem: MenuItem, expandedNodeId: string | null): string {
    if (menuItem.type === MenuItemType.NODE) {
      let classForNode = ' user-node';
      if (String(menuItem.id) === String(expandedNodeId)) {
        classForNode += ' user-node-expanded';
      } else {
        classForNode += ' user-node-collapsed';
      }
      return classForNode;
    }
    return '';
  }

  public isExpandedNodeOrOtherType(menuItem: MenuItem, expandedNodeId: string | null): boolean {
    if (menuItem.type === MenuItemType.NODE) {
      return menuItem.id === expandedNodeId;
    }
    return true;
  }

  public getMenuItem(parentMenuItem: MenuItem, userMenuItem: UserMenuItem): MenuItem {
    const menuItem = new MenuItem();
    menuItem.id = userMenuItem.id;
    menuItem.name = userMenuItem.name;
    menuItem.machineId = userMenuItem.machineId;
    menuItem.firebaseUserId = userMenuItem.firebaseUserId;
    menuItem.index = userMenuItem.index;
    menuItem.isShared = userMenuItem.shared;
    menuItem.parentId = userMenuItem.parentId;
    menuItem.parent = userMenuItem.parent;
    menuItem.menuItemParent = parentMenuItem;
    menuItem.type = userMenuItem.type;
    menuItem.icon = this.designService.getIcon(userMenuItem.type);
    menuItem.pageId = userMenuItem.pageId;
    menuItem.machineCategory = userMenuItem.machineCategory;
    menuItem.organizationId = userMenuItem.organizationId;
    return menuItem;
  }

}
